/**
 * Multi-Factor Authentication (MFA) Type Definitions
 *
 * TypeScript interfaces and types for MFA operations.
 * Used by MFAService, UI components, and AuthProvider.
 *
 * Work Unit: WU3 - Task 4B - Admin MFA Backend Implementation
 * Phase: Phase 1 - Frontend Service Layer
 */

// ===== ERROR HANDLING =====

/**
 * MFA error codes for programmatic handling
 *
 * Maps to Edge Function error_type responses from Phase 2 implementation.
 */
export enum MFAErrorCode {
  // Request validation
  INVALID_REQUEST = 'invalid_request',

  // Authentication
  UNAUTHORIZED = 'unauthorized',
  NOT_REAUTHED = 'not_reauthed',

  // Enrollment
  ALREADY_ENROLLED = 'already_enrolled',
  NOT_ENROLLED = 'not_enrolled',

  // Verification
  INVALID_CODE = 'invalid_code',
  CHALLENGE_EXPIRED = 'challenge_expired',
  CHALLENGE_CONFLICT = 'challenge_conflict',
  CLOCK_SKEW = 'clock_skew',

  // Factor management
  FACTOR_NOT_FOUND = 'factor_not_found',
  DENIED_LAST_FACTOR = 'denied_last_factor',

  // System
  RATE_LIMITED = 'rate_limited',
  FEATURE_DISABLED = 'feature_disabled',
  SERVER_ERROR = 'server_error',
}

/**
 * MFA error object
 *
 * Returned by all MFAService methods when operations fail.
 */
export interface MFAError {
  /** Error code for programmatic handling */
  error_code: MFAErrorCode;

  /** Human-readable error message */
  message: string;

  /** Seconds to wait before retrying (only for RATE_LIMITED) */
  retry_after?: number;

  /** Server timestamp for clock skew detection (only for CLOCK_SKEW) */
  server_time?: string;
}

// ===== MFA FACTORS =====

/**
 * MFA factor types supported by the system
 */
export type FactorType = 'totp';

/**
 * MFA factor status
 */
export type FactorStatus = 'verified' | 'unverified';

/**
 * MFA factor information
 *
 * Represents an enrolled MFA factor (e.g., authenticator app).
 */
export interface Factor {
  /** Unique factor identifier */
  id: string;

  /** Factor type (currently only TOTP) */
  type: FactorType;

  /** Verification status */
  status: FactorStatus;

  /** Auto-generated friendly name from Supabase Auth (e.g., "Authenticator App") */
  friendly_name?: string;

  /** User-customizable label (e.g., "iPhone", "Work Phone", "Backup Authenticator") */
  label?: string;

  /** When this factor was created (ISO 8601) */
  created_at: string;

  /** When this factor was last used for verification (ISO 8601) */
  last_used_at?: string;
}

// ===== ENROLLMENT =====

/**
 * Response from starting MFA enrollment
 *
 * Contains QR code and secret for setting up authenticator app.
 */
export interface EnrollStartResponse {
  /** Factor ID for this enrollment */
  factor_id: string;

  /** Base64-encoded PNG QR code image */
  qr_code: string;

  /** Base32-encoded secret for manual entry */
  secret: string;

  /** OTP URI for QR code (otpauth://totp/...) */
  otp_uri: string;

  /** Challenge ID for verifying enrollment */
  challenge_id: string;

  /** Challenge expiration timestamp (Unix seconds) */
  expires_at: number;
}

// ===== CHALLENGES =====

/**
 * Response from creating an MFA challenge
 *
 * Challenges are required before verifying TOTP codes.
 */
export interface ChallengeResponse {
  /** Challenge ID for verification */
  challenge_id: string;

  /** Challenge expiration timestamp (Unix seconds) */
  expires_at: number;

  /** Challenge creation timestamp (Unix milliseconds) */
  created_at: number;

  /** Factor ID used for this challenge */
  factor_id: string;
}

// ===== RESULT TYPES =====

/**
 * Successful operation result
 */
export type MFAOk<T> = {
  success: true;
  data: T;
};

/**
 * Failed operation result
 */
export type MFAErr = {
  success: false;
  error: MFAError;
};

/**
 * Result type for all MFA operations
 *
 * Discriminated union that allows type-safe pattern matching:
 *
 * @example
 * const result = await mfaService.enrollStart();
 * if (!result.success) {
 *   console.error(result.error.message);
 *   return;
 * }
 * // TypeScript knows result.data is EnrollStartResponse
 * const { qr_code, secret } = result.data;
 */
export type MFAResult<T> = MFAOk<T> | MFAErr;

// ===== RE-AUTHENTICATION =====

/**
 * Response from touching re-authentication timestamp
 */
export interface ReauthTouchResponse {
  /** Updated re-authentication timestamp (ISO 8601) */
  reauth_at: string;
}
